import { Money, MoneySchema } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import { CurrencyConverter } from "@redotech/money/exchange";
import {
  AmazonServices,
  ApcServices,
  CanadaPostServices,
  Carriers,
  DhlEcsServices,
  DhlExpressServices,
  EPostGlobalServices,
  FedexServices,
  PurolatorServices,
  ServiceLevel,
  SpeeDeeServices,
  UpsServices,
  UspsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import * as zExt from "@redotech/rpc/ext";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { rateTableTextFormatSchema } from "../../conversion/delivery-date/rate-table-text-format";
import {
  PriceConstraintKind,
  PriceConstraintSchema,
} from "./rate-table-constraints";

const shippingDiscountSchema_ = z.object({
  amount: z
    .object({
      currency: createEnum(Currency, { enumName: "Currency" }),
      value: z.string(),
    })
    .optional(),
  percentage: z.string().optional(),
});
export const shippingDiscountSchema: z.ZodType<ShippingDiscount> =
  shippingDiscountSchema_;
export interface ShippingDiscount
  extends z.infer<typeof shippingDiscountSchema_> {}

const shippingDiscountRuleSchema_ = z.object({
  cartPriceConditions: z
    .object({
      minCartPrice: z.number().min(0).nullable(),
      maxCartPrice: z.number().min(0).nullable(),
      currency: createEnum(Currency, { enumName: "Currency" }),
    })
    .optional(),
  discount: shippingDiscountSchema_,
});
export const shippingDiscountRuleSchema: z.ZodType<ShippingDiscountRule> =
  shippingDiscountRuleSchema_;
export interface ShippingDiscountRule
  extends z.infer<typeof shippingDiscountRuleSchema_> {}

export enum RateTableType {
  FIXED = "fixed",
  DYNAMIC = "dynamic",
  CUSTOM = "custom",
}

export enum ConditionalPricingCondition {
  WEIGHT = "weight",
  PRICE = "price",
}

/**
 * Preset options for how to select the rate to show from multiple rates.
 */
export enum DynamicRateSelectionPreset {
  CHEAPEST = "cheapest",
  FASTEST = "fastest",
  CUSTOM = "custom",
}

/**
 * The method to use to select the rate to show from multiple rates.
 */
export enum DynamicRateSelectionMethod {
  CHEAPEST = "cheapest",
  FASTEST = "fastest",
}

export enum CustomDynamicRateSelectionConditionKind {
  DELIVERY_DAYS = "delivery_days",
}

/**
 * Comparison operators that can be used for numeric conditions
 */
export enum NumericComparison {
  LESS_THAN = "less_than",
  LESS_THAN_OR_EQUAL = "less_than_or_equal",
  GREATER_THAN = "greater_than",
  GREATER_THAN_OR_EQUAL = "greater_than_or_equal",
  EQUAL_TO = "equal_to",
}

// Should always be in sync with the data repository's copy of the enum
// `metaflow/flows/mercury_model/src/common/rounding.py`
export enum DynamicRateRounding {
  /**
   * E.g. 1.13 -> 2.00
   */
  NEAREST_WHOLE = "NEAREST_WHOLE",
  /**
   * E.g. 1.13 -> 1.99
   */
  NEAREST_WHOLE_MINUS_ONE = "NEAREST_WHOLE_MINUS_ONE",
  /**
   * E.g. 1.13 -> 1.95
   */
  NEAREST_WHOLE_MINUS_FIVE = "NEAREST_WHOLE_MINUS_FIVE",
  /**
   * E.g. 1.13 -> 1.50
   */
  NEAREST_HALF = "NEAREST_HALF",
  /**
   * E.g. 1.13 -> 1.25
   */
  NEAREST_QUARTER = "NEAREST_QUARTER",
  /**
   * E.g. 1.13 -> 1.20
   */
  NEAREST_TENTH = "NEAREST_TENTH",
  /**
   * E.g. 1.13 -> 1.15
   */
  NEAREST_TWENTIETH = "NEAREST_TWENTIETH",
}

export const dynamicRateRoundingToLabel: Record<DynamicRateRounding, string> = {
  [DynamicRateRounding.NEAREST_WHOLE]: "Nearest whole",
  [DynamicRateRounding.NEAREST_WHOLE_MINUS_ONE]: "Nearest whole (minus one)",
  [DynamicRateRounding.NEAREST_WHOLE_MINUS_FIVE]: "Nearest whole (minus five)",
  [DynamicRateRounding.NEAREST_HALF]: "Nearest half",
  [DynamicRateRounding.NEAREST_QUARTER]: "Nearest quarter",
  [DynamicRateRounding.NEAREST_TENTH]: "Nearest tenth",
  [DynamicRateRounding.NEAREST_TWENTIETH]: "Nearest twentieth",
};

export const dynamicRateRoundingToExample: Record<DynamicRateRounding, string> =
  {
    [DynamicRateRounding.NEAREST_WHOLE]: "1.13 -> 2.00",
    [DynamicRateRounding.NEAREST_WHOLE_MINUS_ONE]: "1.13 -> 1.99",
    [DynamicRateRounding.NEAREST_WHOLE_MINUS_FIVE]: "1.13 -> 1.95",
    [DynamicRateRounding.NEAREST_HALF]: "1.13 -> 1.50",
    [DynamicRateRounding.NEAREST_QUARTER]: "1.13 -> 1.25",
    [DynamicRateRounding.NEAREST_TENTH]: "1.13 -> 1.20",
    [DynamicRateRounding.NEAREST_TWENTIETH]: "1.13 -> 1.15",
  };

const roundingKey = ["0", "1", "2", "3", "4", "5", "6"] as const;
type RoundingKey = (typeof roundingKey)[number];
export function isDynamicRateRoundingServiceCodeSerializedKey(
  key: string,
): key is RoundingKey {
  return roundingKey.includes(key as RoundingKey);
}
export const dynamicRateRoundingToServiceCodeDeserialization: Record<
  (typeof roundingKey)[number],
  DynamicRateRounding
> = {
  // CAUTION: Do not change these constants. If you do, carefully consider
  // backwards compatibility. Adding new ones is fine. Do not remove or modify
  // existing ones.
  ["0"]: DynamicRateRounding.NEAREST_WHOLE,
  ["5"]: DynamicRateRounding.NEAREST_WHOLE_MINUS_ONE,
  ["6"]: DynamicRateRounding.NEAREST_WHOLE_MINUS_FIVE,
  ["1"]: DynamicRateRounding.NEAREST_HALF,
  ["2"]: DynamicRateRounding.NEAREST_QUARTER,
  ["3"]: DynamicRateRounding.NEAREST_TENTH,
  ["4"]: DynamicRateRounding.NEAREST_TWENTIETH,
};

const ShippingRateLocationSchema_ = z.object({
  country: z.string(),
  provinces: z.array(z.string()),
});
export const ShippingRateLocationSchema: z.ZodType<ShippingRateLocation> =
  ShippingRateLocationSchema_;
export interface ShippingRateLocation
  extends z.infer<typeof ShippingRateLocationSchema_> {}

export type Rate = FixedRate | DynamicRate;

const WeightConditionSchema = z.object({
  type: z.literal(ConditionalPricingCondition.WEIGHT),
  minGrams: z.number().min(0),
  maxGrams: z.number().min(0),
});
export type WeightCondition = z.infer<typeof WeightConditionSchema>;

const PriceConditionSchema = z.object({
  type: z.literal(ConditionalPricingCondition.PRICE),
  minPrice: z.number().min(0),
  maxPrice: z.number().min(0),
  currency: z.string(),
});
export type PriceCondition = z.infer<typeof PriceConditionSchema>;

const RateConditionSchema_ = z.union([
  WeightConditionSchema,
  PriceConditionSchema,
]);
export const RateConditionSchema: z.ZodType<RateCondition> =
  RateConditionSchema_;
export type RateCondition = z.infer<typeof RateConditionSchema_>;

/**
 * Base schema for all custom rate selection conditions
 */
const BaseCustomDynamicRateSelectionConditionSchema = z.object({
  selectionMethod: createEnum(DynamicRateSelectionMethod, {
    enumName: "DynamicRateSelectionMethod",
  }),
});

/**
 * Delivery days condition schema
 */
const DeliveryDaysConditionSchema_ =
  BaseCustomDynamicRateSelectionConditionSchema.extend({
    kind: z.literal(CustomDynamicRateSelectionConditionKind.DELIVERY_DAYS),
    comparison: createEnum(NumericComparison, {
      enumName: "NumericComparison",
    }),
    threshold: z.number(),
  });
export const DeliveryDaysConditionSchema: z.ZodType<DeliveryDaysCondition> =
  DeliveryDaysConditionSchema_;
export interface DeliveryDaysCondition
  extends z.infer<typeof DeliveryDaysConditionSchema_> {}

const CustomDynamicRateSelectionConditionSchema_ = z.discriminatedUnion(
  "kind",
  [DeliveryDaysConditionSchema_],
);
export const CustomDynamicRateSelectionConditionSchema: z.ZodType<CustomDynamicRateSelectionCondition> =
  CustomDynamicRateSelectionConditionSchema_;
export interface CustomDynamicRateSelectionCondition
  extends z.infer<typeof CustomDynamicRateSelectionConditionSchema_> {}

const CustomDynamicRateSelectionConfigSchema_ = z.object({
  orderedConditions: z.array(CustomDynamicRateSelectionConditionSchema_),
  fallbackSelectionMethod: createEnum(DynamicRateSelectionMethod, {
    enumName: "DynamicRateSelectionMethod",
  }),
});
export const CustomDynamicRateSelectionConfigSchema: z.ZodType<CustomDynamicRateSelectionConfig> =
  CustomDynamicRateSelectionConfigSchema_;
export interface CustomDynamicRateSelectionConfig
  extends z.infer<typeof CustomDynamicRateSelectionConfigSchema_> {}

export const DEFAULT_CUSTOM_RATE_SELECTION_CONFIG: CustomDynamicRateSelectionConfig =
  {
    orderedConditions: [],
    fallbackSelectionMethod: DynamicRateSelectionMethod.CHEAPEST,
  } as const;

const BaseRateSchema = z.object({
  enabled: z.boolean().optional(),
  merchantPaidCoverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
});

const BaseShippingRateTableSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  description: z.string(),
  fallbackName: z.string().optional(),
  fallbackDescription: z.string().optional(),
  code: z.string(),
  type: createEnum(RateTableType, { enumName: "RateTableType" }),
  originLocations: z.array(ShippingRateLocationSchema_),
  destinationLocations: z.array(ShippingRateLocationSchema_),
  coverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
  orderTags: z.array(z.string()).optional(),
  shipmentTagIds: z.array(z.string()).nullish(),
  shippingDiscountRules: z.array(shippingDiscountRuleSchema_).optional(),
  deliveryProfile: z.object({ id: z.string(), name: z.string() }).optional(),
  priceConstraints: PriceConstraintSchema.nullish(),
  shippingOptionWidgetId: z.string().optional(),
  /**
   * Optional merchant-configured service code used as the resolved service_code
   * in Shopify shipping option metafields. Distinct from `code`, which is the
   * internal rate table identifier used for delivery customization matching.
   */
  serviceCode: z.string().optional(),
});

const FixedRateSchema = BaseRateSchema.extend({
  price: z.string(),
  conditions: z.array(RateConditionSchema),
  currency: z.string(),
});
export type FixedRate = z.infer<typeof FixedRateSchema>;

const FixedShippingRateTableSchema = BaseShippingRateTableSchema.extend({
  type: z.literal(RateTableType.FIXED),
  rates: z.array(FixedRateSchema).min(1),
});
export type FixedShippingRateTable = z.infer<
  typeof FixedShippingRateTableSchema
>;
const DisplayedFixedShippingRateTableSchema = FixedShippingRateTableSchema.omit(
  { _id: true, originLocations: true, destinationLocations: true, code: true },
).extend({ id: z.string() });
export type DisplayedFixedShippingRateTable = z.infer<
  typeof DisplayedFixedShippingRateTableSchema
>;

const carrierAndServiceSchema_ = z.union([
  z.object({
    carrier: z.literal(Carriers.FEDEX),
    service: createEnum(FedexServices, { enumName: "FedexServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.UPS),
    service: createEnum(UpsServices, { enumName: "UpsServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.USPS),
    service: createEnum(UspsServices, { enumName: "UspsServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.AMAZON),
    service: createEnum(AmazonServices, { enumName: "AmazonServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.DHL_EXPRESS),
    service: createEnum(DhlExpressServices, { enumName: "DhlExpressServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.DHL_ECS),
    service: createEnum(DhlEcsServices, { enumName: "DhlEcsServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.SPEE_DEE),
    service: createEnum(SpeeDeeServices, { enumName: "SpeeDeeServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.PUROLATOR),
    service: createEnum(PurolatorServices, { enumName: "PurolatorServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.CANADA_POST),
    service: createEnum(CanadaPostServices, { enumName: "CanadaPostServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.EPOST_GLOBAL),
    service: createEnum(EPostGlobalServices, {
      enumName: "EPostGlobalServices",
    }),
  }),
  z.object({
    carrier: z.literal(Carriers.APC),
    service: createEnum(ApcServices, { enumName: "ApcServices" }),
  }),
]);
export const carrierAndServiceSchema: z.ZodType<CarrierAndService> =
  carrierAndServiceSchema_;
export type CarrierAndService = z.infer<typeof carrierAndServiceSchema_>;

/**
 * When we paired carriers and services, we decided to include services from
 * multiple carriers in the same service enum. For example, the `FedexServices`
 * enum includes services from both FedEx and FedExSmartPost carriers.
 *
 * As a quick fix to having to map from every carrier to every service (which
 * is likely the ideal solution), we consider certain carriers to be
 * equivalent. If we end up creating the ideal mapping, we need to write a
 * migration to update the carrier/service pairs saved in the database for
 * rateTables in checkout experiences to be correct.
 */
const fedexEquivalents = new Set([Carriers.FEDEX, Carriers.FEDEX_SMART_POST]);
const upsEquivalents = new Set([Carriers.UPS, Carriers.UPS_SUREPOST]);

export const carrierFromCarrierAndServiceToEquivalentAllowedCarriersForCheckoutRates: Record<
  Carriers,
  Set<Carriers>
> = {
  [Carriers.FEDEX]: fedexEquivalents,
  [Carriers.FEDEX_SMART_POST]: fedexEquivalents,
  [Carriers.UPS]: upsEquivalents,
  [Carriers.UPS_SUREPOST]: upsEquivalents,
  [Carriers.UPS_MAIL_INNOVATIONS]: new Set([Carriers.UPS_MAIL_INNOVATIONS]),
  [Carriers.USPS]: new Set([Carriers.USPS]),
  [Carriers.USPS_RETURNS]: new Set([Carriers.USPS_RETURNS]),
  [Carriers.USPS_SHIP]: new Set([Carriers.USPS_SHIP]),
  [Carriers.AMAZON]: new Set([Carriers.AMAZON]),
  [Carriers.DHL_EXPRESS]: new Set([Carriers.DHL_EXPRESS]),
  [Carriers.DHL_ECS]: new Set([Carriers.DHL_ECS]),
  [Carriers.VEHO]: new Set([Carriers.VEHO]),
  [Carriers.APC]: new Set([Carriers.APC]),
  [Carriers.FIRST_MILE]: new Set([Carriers.FIRST_MILE]),
  [Carriers.AUSTRALIA_POST]: new Set([Carriers.AUSTRALIA_POST]),
  [Carriers.CANADA_POST]: new Set([Carriers.CANADA_POST]),
  [Carriers.GLS]: new Set([Carriers.GLS]),
  [Carriers.ASENDIA]: new Set([Carriers.ASENDIA]),
  [Carriers.ROYAL_MAIL_V3]: new Set([Carriers.ROYAL_MAIL_V3]),
  [Carriers.SPEE_DEE]: new Set([Carriers.SPEE_DEE]),
  [Carriers.PASSPORT_GLOBAL]: new Set([Carriers.PASSPORT_GLOBAL]),
  [Carriers.EPOST_GLOBAL]: new Set([Carriers.EPOST_GLOBAL]),
  [Carriers.LANDMARK_GLOBAL]: new Set([Carriers.LANDMARK_GLOBAL]),
  [Carriers.ETA_EXPRESS]: new Set([Carriers.ETA_EXPRESS]),
  [Carriers.ONTRAC]: new Set([Carriers.ONTRAC]),
  [Carriers.GLOBALE]: new Set([Carriers.GLOBALE]),
  [Carriers.PUROLATOR]: new Set([Carriers.PUROLATOR]),
  [Carriers.UNIUNI]: new Set([Carriers.UNIUNI]),
  [Carriers.STALLION_EXPRESS]: new Set([Carriers.STALLION_EXPRESS]),
  [Carriers.CHITCHATS]: new Set([Carriers.CHITCHATS]),
  [Carriers.POST_NL]: new Set([Carriers.POST_NL]),
  [Carriers.INTELCOM]: new Set([Carriers.INTELCOM]),
  [Carriers.FLEET_OPTICS]: new Set([Carriers.FLEET_OPTICS]),
  [Carriers.ICS_COURIER]: new Set([Carriers.ICS_COURIER]),
  [Carriers.WHIZ_DELIVERY]: new Set([Carriers.WHIZ_DELIVERY]),
  [Carriers.TIKTOK_SHOP]: new Set([Carriers.TIKTOK_SHOP]),
  [Carriers.CANPAR_EXPRESS]: new Set([Carriers.CANPAR_EXPRESS]),
};

const DynamicRateSchema = BaseRateSchema.extend({
  markup: z
    .object({
      amount: z.string().optional(),
      percentage: z.string().optional(),
    })
    .optional(),
  carrierService: carrierAndServiceSchema_,
});
export type DynamicRate = z.infer<typeof DynamicRateSchema>;

const DynamicShippingRateTableObjectSchema = BaseShippingRateTableSchema.extend(
  {
    type: z.literal(RateTableType.DYNAMIC),
    serviceLevelBucketedServiceSelection: createEnum(ServiceLevel, {
      enumName: "ServiceLevel",
    }).optional(),
    markup: z
      .object({
        amount: z
          .object({
            currency: createEnum(Currency, { enumName: "Currency" }),
            value: z.string(),
          })
          .optional(),
        percentage: z.string().optional(),
      })
      .optional(),
    rounding: createEnum(DynamicRateRounding, {
      enumName: "DynamicRateRounding",
    }).nullish(),
    rates: z.array(DynamicRateSchema),
    multipleDynamicRateSelection: createEnum(DynamicRateSelectionPreset, {
      enumName: "DynamicRateSelectionPreset",
    }).optional(),
    /**
     * Custom rate selection config used when multipleDynamicRateSelection is
     * CUSTOM.
     */
    customDynamicRateSelectionConfig:
      CustomDynamicRateSelectionConfigSchema.optional(),
    fulfillmentDelayDaysOverride: z.number().optional(),
    conditions: z.array(RateConditionSchema).optional(),
    excludedCarriers: z
      .array(createEnum(Carriers, { enumName: "Carriers" }))
      .optional(),
    /**
     * If true, when this rate is purchased, the Temporal activity to apply the
     * checkout rate purchased will be skipped when the fulfillment group is
     * processed into the Redo OMS.
     */
    skipOmsApplyCheckoutRatePurchased: z.boolean().optional(),
    priceBounds: z
      .object({
        currency: createEnum(Currency, { enumName: "Currency" }),
        minimum: z.string().optional(),
        maximum: z.string().optional(),
      })
      .refine((data) => isDef(data.minimum) || isDef(data.maximum), {
        message:
          "priceBounds should not exist if neither minimum or maximum is defined.",
      })
      .refine(
        (data) => {
          if (isDef(data.minimum) && isDef(data.maximum)) {
            return Number(data.minimum) <= Number(data.maximum);
          }

          return true;
        },
        {
          message:
            "Minimum price must be less than maximum price if both exist.",
        },
      )
      .optional(),
    rateTableTextFormat: rateTableTextFormatSchema.optional(),
    /**
     * Snapshot used to synthesize a synchronous last-resort rate result when the
     * live dynamic rate computation exceeds its deadline. Unrelated to
     * `fallbackName`/`fallbackDescription`, which control customer-name display.
     */
    fallback: z
      .object({
        price: z
          .object({
            /**
             * Manually configured price override. When set, takes precedence
             * over the per-service-level cold-start defaults in
             * `pickFallbackPrice`. The nested `price` object is structured this
             * way to leave room for cron-populated observed snapshots and
             * non-price fields (delivery estimate, etc.) without reshaping the
             * schema later.
             */
            override: MoneySchema.nullish(),
          })
          .optional(),
      })
      .nullish(),
  },
);

// When multipleDynamicRateSelection is CUSTOM, customDynamicRateSelectionConfig
// must be present. Applied via a helper because Zod 4 forbids `.omit()`/`.extend()`
// on a schema that already carries a top-level refinement, and the omitted/extended
// variants below need to share this rule.
const refineDynamicRateSelection = <
  Schema extends z.ZodType<{
    multipleDynamicRateSelection?: DynamicRateSelectionPreset | undefined;
    customDynamicRateSelectionConfig?: unknown;
  }>,
>(
  schema: Schema,
) =>
  schema.refine(
    (data) => {
      if (
        data.multipleDynamicRateSelection === DynamicRateSelectionPreset.CUSTOM
      ) {
        return isDef(data.customDynamicRateSelectionConfig);
      }
      return true;
    },
    {
      message:
        "customDynamicRateSelectionConfig is required when multipleDynamicRateSelection is CUSTOM",
      path: ["customDynamicRateSelectionConfig"],
    },
  );

const DynamicShippingRateTableSchema = refineDynamicRateSelection(
  DynamicShippingRateTableObjectSchema,
);
export type DynamicShippingRateTable = z.infer<
  typeof DynamicShippingRateTableSchema
>;
const DisplayedDynamicShippingRateTableSchema = refineDynamicRateSelection(
  DynamicShippingRateTableObjectSchema.omit({
    _id: true,
    originLocations: true,
    destinationLocations: true,
    code: true,
  }).extend({ id: z.string() }),
);
export type DisplayedDynamicShippingRateTable = z.infer<
  typeof DisplayedDynamicShippingRateTableSchema
>;

/**
 * A CUSTOM rate table delegates per-table rate production to a merchant-authored
 * sandboxed JavaScript artifact (see `checkout.shipping-method.custom-rate-table`
 * contract in @redotech/redo-extensions-common). The platform still owns zone
 * gating, currency conversion, price-constraint clamping, and service-code
 * generation; the artifact owns rate selection, pricing, and the per-rate
 * shape returned to checkout.
 *
 * `artifactId` references an `extension_artifact` row scoped to this team.
 */
const CustomShippingRateTableSchema = BaseShippingRateTableSchema.extend({
  type: z.literal(RateTableType.CUSTOM),
  artifactId: z.string(),
});
export type CustomShippingRateTable = z.infer<
  typeof CustomShippingRateTableSchema
>;
const DisplayedCustomShippingRateTableSchema =
  CustomShippingRateTableSchema.omit({
    _id: true,
    originLocations: true,
    destinationLocations: true,
    code: true,
  }).extend({ id: z.string() });
export type DisplayedCustomShippingRateTable = z.infer<
  typeof DisplayedCustomShippingRateTableSchema
>;

const ShippingRateTableSchema_ = z.discriminatedUnion("type", [
  FixedShippingRateTableSchema,
  DynamicShippingRateTableSchema,
  CustomShippingRateTableSchema,
]);
export const ShippingRateTableSchema: z.ZodType<ShippingRateTable> =
  ShippingRateTableSchema_;
export type ShippingRateTable = z.infer<typeof ShippingRateTableSchema_>;

const DisplayedShippingRateTableSchema_ = z.discriminatedUnion("type", [
  DisplayedFixedShippingRateTableSchema,
  DisplayedDynamicShippingRateTableSchema,
  DisplayedCustomShippingRateTableSchema,
]);
export const DisplayedShippingRateTableSchema: z.ZodType<DisplayedShippingRateTable> =
  DisplayedShippingRateTableSchema_;
export type DisplayedShippingRateTable = z.infer<
  typeof DisplayedShippingRateTableSchema_
>;

export const ShippingRateTableArraySchema: z.ZodType<ShippingRateTable[]> =
  z.array(ShippingRateTableSchema);

const CreateFixedShippingRateTableInputSchema =
  FixedShippingRateTableSchema.omit({
    _id: true,
    code: true,
    originLocations: true,
    destinationLocations: true,
  });
export type CreateFixedShippingRateTableInput = z.infer<
  typeof CreateFixedShippingRateTableInputSchema
>;

const CreateDynamicShippingRateTableInputSchema = refineDynamicRateSelection(
  DynamicShippingRateTableObjectSchema.omit({
    _id: true,
    code: true,
    originLocations: true,
    destinationLocations: true,
  }),
);
export type CreateDynamicShippingRateTableInput = z.infer<
  typeof CreateDynamicShippingRateTableInputSchema
>;

const CreateCustomShippingRateTableInputSchema =
  CustomShippingRateTableSchema.omit({
    _id: true,
    code: true,
    originLocations: true,
    destinationLocations: true,
  });
export type CreateCustomShippingRateTableInput = z.infer<
  typeof CreateCustomShippingRateTableInputSchema
>;

const CreateShippingRateTableInputSchema_ = z.discriminatedUnion("type", [
  CreateFixedShippingRateTableInputSchema,
  CreateDynamicShippingRateTableInputSchema,
  CreateCustomShippingRateTableInputSchema,
]);
export const CreateShippingRateTableInputSchema: z.ZodType<CreateShippingRateTableInput> =
  CreateShippingRateTableInputSchema_;
export type CreateShippingRateTableInput = z.infer<
  typeof CreateShippingRateTableInputSchema_
>;
export interface ICreateShippingRateTableInput
  extends z.ZodType<CreateShippingRateTableInput> {}

export function isFixedRateTable(
  rateTable: ShippingRateTable,
): rateTable is FixedShippingRateTable {
  return rateTable.type === RateTableType.FIXED;
}

export function isDisplayedFixedRateTable(
  rateTable: DisplayedShippingRateTable,
): rateTable is DisplayedFixedShippingRateTable {
  return rateTable.type === RateTableType.FIXED;
}

export function isDynamicRateTable(
  rateTable: ShippingRateTable,
): rateTable is DynamicShippingRateTable {
  return rateTable.type === RateTableType.DYNAMIC;
}

export function isDisplayedDynamicRateTable(
  rateTable: DisplayedShippingRateTable,
): rateTable is DisplayedDynamicShippingRateTable {
  return rateTable.type === RateTableType.DYNAMIC;
}

export function isCustomRateTable(
  rateTable: ShippingRateTable,
): rateTable is CustomShippingRateTable {
  return rateTable.type === RateTableType.CUSTOM;
}

export function isDisplayedCustomRateTable(
  rateTable: DisplayedShippingRateTable,
): rateTable is DisplayedCustomShippingRateTable {
  return rateTable.type === RateTableType.CUSTOM;
}

type AnyRateInTable = FixedRate | DynamicRate;

export function isFixedRate(rate: AnyRateInTable): rate is FixedRate {
  return "price" in rate;
}

export function isDynamicRate(rate: AnyRateInTable): rate is DynamicRate {
  return "markup" in rate;
}

export function isWeightCondition(
  condition: RateCondition,
): condition is WeightCondition {
  return condition.type === ConditionalPricingCondition.WEIGHT;
}

export function isPriceCondition(
  condition: RateCondition,
): condition is PriceCondition {
  return condition.type === ConditionalPricingCondition.PRICE;
}

export function evaluateRateTableConditions({
  initialConditions,
  order,
  exchangeRates,
}: {
  initialConditions: RateCondition[];
  order: { totalPricePresentment: Money; totalGrams: number };
  exchangeRates: CurrencyConverter;
}): boolean {
  const conditions = initialConditions.map((condition) => {
    if (condition.type === ConditionalPricingCondition.PRICE) {
      return {
        ...condition,
        exchangeRate: order.totalPricePresentment.currency
          ? exchangeRates.exchangeRate(
              order.totalPricePresentment.currency,
              condition.currency as Currency,
            )
          : 1,
      };
    }
    return condition;
  });

  const matchesConditions = doesRateMatchConditions({
    conditions,
    orderTotalValue: order.totalPricePresentment.amount,
    orderTotalWeightInGrams: order.totalGrams,
  });

  return matchesConditions;
}

export function doesRateMatchConditions({
  conditions,
  orderTotalValue,
  orderTotalWeightInGrams,
}: {
  conditions: FixedShippingRateConditionWithExchangeRate[];
  orderTotalValue: number;
  orderTotalWeightInGrams: number;
}) {
  let ratePassesAllConditions = true;

  for (const condition of conditions) {
    let conditionPassed = false;
    switch (condition.type) {
      case ConditionalPricingCondition.PRICE: {
        const priceAfterExchangeRate = orderTotalValue * condition.exchangeRate;
        conditionPassed =
          priceAfterExchangeRate >= condition.minPrice &&
          priceAfterExchangeRate < condition.maxPrice;
        break;
      }
      case ConditionalPricingCondition.WEIGHT:
        conditionPassed =
          orderTotalWeightInGrams >= condition.minGrams &&
          orderTotalWeightInGrams < condition.maxGrams;
        break;
      default:
        assertNever(condition);
    }

    if (!conditionPassed) {
      ratePassesAllConditions = false;
      break;
    }
  }

  return ratePassesAllConditions;
}

export function isUnconstrainedStandaloneDynamicRateTableWithServiceLevel({
  rateTable,
  otherRateTables,
}: {
  rateTable: ShippingRateTable | DisplayedShippingRateTable;
  otherRateTables: ShippingRateTable[] | DisplayedShippingRateTable[];
}): boolean {
  const shippingSystemRateTableIds = new Set<string>();
  for (const _rateTable of [rateTable, ...otherRateTables]) {
    if (
      _rateTable.priceConstraints?.kind ===
      PriceConstraintKind.RELATIVE_TO_OTHER_RATE_TABLE
    ) {
      shippingSystemRateTableIds.add(
        _rateTable.priceConstraints.priceConstraints.relativeToRateTableId,
      );
      shippingSystemRateTableIds.add(
        "id" in _rateTable ? _rateTable.id : _rateTable._id.toString(),
      );
    }
  }

  const rateTableIsStandalone = !shippingSystemRateTableIds.has(
    "id" in rateTable ? rateTable.id : rateTable._id.toString(),
  );
  if (!rateTableIsStandalone) {
    return false;
  }

  const rateTableIsDynamic = rateTable.type === RateTableType.DYNAMIC;
  if (!rateTableIsDynamic) {
    return false;
  }

  const rateTableHasServiceLevel = isDef(
    rateTable.serviceLevelBucketedServiceSelection,
  );
  if (!rateTableHasServiceLevel) {
    return false;
  }

  const rateTableHasFixedPrice =
    rateTable.priceConstraints?.kind === PriceConstraintKind.DEFAULT &&
    ((isDef(rateTable.priceConstraints?.priceConstraints?.atLeast?.amount) &&
      isDef(rateTable.priceConstraints?.priceConstraints?.atMost?.amount) &&
      rateTable.priceConstraints.priceConstraints.atLeast.amount ===
        rateTable.priceConstraints.priceConstraints.atMost.amount) ||
      rateTable.priceConstraints?.priceConstraints?.atMost?.amount === 0);
  if (rateTableHasFixedPrice) {
    return false;
  }

  return true;
}

type FixedShippingRateConditionWithExchangeRate =
  | (Extract<
      FixedShippingRateTable["rates"][number]["conditions"][number],
      { type: ConditionalPricingCondition.PRICE }
    > & { exchangeRate: number })
  | Exclude<
      FixedShippingRateTable["rates"][number]["conditions"][number],
      { type: ConditionalPricingCondition.PRICE }
    >;

export enum SkippedZoneReason {
  INTERNATIONAL = "international",
  NO_FIXED_RATES = "no_fixed_rates",
}

const SkippedZoneSchema_ = z.object({
  zoneName: z.string(),
  reason: z.enum([
    SkippedZoneReason.INTERNATIONAL,
    SkippedZoneReason.NO_FIXED_RATES,
  ]),
  profileName: z.string(),
});
export const SkippedZoneSchema: z.ZodType<SkippedZone> = SkippedZoneSchema_;

export interface SkippedZone extends z.infer<typeof SkippedZoneSchema_> {}
